import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import VendorLayout from '../../components/VendorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  RotateCcw, Search, Loader2, Package, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, AlertTriangle, IndianRupee, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPrice } from '../../lib/utils';

export default function VendorReturns() {
  const { vendor, token } = useVendor();
  const [returns, setReturns] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedReturn, setExpandedReturn] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const navigate = useNavigate();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Authentication is handled by VendorProtectedRoute
    if (vendor && token) {
      fetchReturns();
    }
  }, [vendor, token, filter]);

  const fetchReturns = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API_URL}/api/vendor/returns`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setReturns(response.data.returns || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0, completed: 0 });
    } catch (error) {
      console.error('Error fetching returns:', error);
      toast.error('Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (returnId) => {
    setActionLoading(returnId);
    try {
      await axios.put(
        `${API_URL}/api/vendor/returns/${returnId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Return approved!');
      fetchReturns();
    } catch (error) {
      console.error('Error approving return:', error);
      toast.error('Failed to approve return');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (returnId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setActionLoading(returnId);
    try {
      await axios.put(
        `${API_URL}/api/vendor/returns/${returnId}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Return rejected');
      setShowRejectModal(null);
      setRejectReason('');
      fetchReturns();
    } catch (error) {
      console.error('Error rejecting return:', error);
      toast.error('Failed to reject return');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (returnId) => {
    setActionLoading(returnId);
    try {
      await axios.put(
        `${API_URL}/api/vendor/returns/${returnId}/complete`,
        { refund_reference: `REF-${Date.now()}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Return completed - Refund processed!');
      fetchReturns();
    } catch (error) {
      console.error('Error completing return:', error);
      toast.error('Failed to complete return');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, label: 'Pending' },
      approved: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: 'Rejected' },
      completed: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'Completed' }
    };
    return configs[status] || configs.pending;
  };

  const filteredReturns = returns.filter(ret => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ret.id?.toLowerCase().includes(search) ||
      ret.customer_name?.toLowerCase().includes(search) ||
      ret.order_id?.toLowerCase().includes(search)
    );
  });

  const statusFilters = [
    { value: 'all', label: 'All Returns' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' }
  ];

  return (
    <VendorLayout>
      <div className="space-y-6" data-testid="vendor-returns-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <RotateCcw className="w-7 h-7 text-orange-600" />
            Returns Management
          </h1>
          <p className="text-gray-500">Handle product return requests from customers</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
                onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer transition-all ${filter === 'approved' ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setFilter(filter === 'approved' ? 'all' : 'approved')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-blue-600">{counts.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer transition-all ${filter === 'completed' ? 'ring-2 ring-green-500' : ''}`}
                onClick={() => setFilter(filter === 'completed' ? 'all' : 'completed')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{counts.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`cursor-pointer transition-all ${filter === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
                onClick={() => setFilter(filter === 'rejected' ? 'all' : 'rejected')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">{counts.rejected}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by return ID, customer, or order..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="return-search"
          />
        </div>

        {/* Returns List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          </div>
        ) : filteredReturns.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <RotateCcw className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No return requests</h3>
              <p className="text-gray-500">Return requests will appear here when customers request them</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredReturns.map((ret) => {
              const statusConfig = getStatusConfig(ret.status);
              const StatusIcon = statusConfig.icon;
              const isExpanded = expandedReturn === ret.id;
              
              return (
                <Card key={ret.id} className="overflow-hidden" data-testid={`return-card-${ret.id}`}>
                  <CardContent className="p-0">
                    {/* Return Header */}
                    <div 
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedReturn(isExpanded ? null : ret.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${statusConfig.color}`}>
                          <StatusIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            Return #{ret.id?.slice(-8).toUpperCase()}
                          </p>
                          <p className="text-sm text-gray-500">
                            Order #{ret.order_id?.slice(-8).toUpperCase()} • {ret.customer_name}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-orange-600">{formatPrice(ret.refund_amount)}</p>
                          <p className="text-sm text-gray-500">Refund Amount</p>
                        </div>
                        <Badge className={`${statusConfig.color} border`}>
                          {statusConfig.label}
                        </Badge>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t bg-gray-50 p-4">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Return Reason */}
                          <div className="space-y-3">
                            <h4 className="font-medium text-gray-700">Return Reason</h4>
                            <div className="p-3 bg-white rounded-lg border">
                              <Badge className="mb-2 bg-orange-100 text-orange-800">
                                {ret.reason_category || 'Other'}
                              </Badge>
                              <p className="text-sm text-gray-600">{ret.reason || 'No reason provided'}</p>
                            </div>
                          </div>

                          {/* Customer Info */}
                          <div className="space-y-3">
                            <h4 className="font-medium text-gray-700">Customer Details</h4>
                            <div className="p-3 bg-white rounded-lg border space-y-1 text-sm">
                              <p className="font-medium">{ret.customer_name}</p>
                              <p className="text-gray-500">{ret.customer_phone}</p>
                              {ret.customer_email && <p className="text-gray-500">{ret.customer_email}</p>}
                            </div>
                          </div>
                        </div>

                        {/* Return Items */}
                        {ret.items && ret.items.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-medium text-gray-700 mb-2">Items to Return</h4>
                            <div className="space-y-2">
                              {ret.items.map((item, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                  <div className="flex items-center gap-3">
                                    {item.image ? (
                                      <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                                    ) : (
                                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                        <Package className="w-6 h-6 text-gray-400" />
                                      </div>
                                    )}
                                    <div>
                                      <p className="font-medium text-gray-900">{item.name}</p>
                                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                                    </div>
                                  </div>
                                  <p className="font-semibold">{formatPrice(item.price * item.quantity)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Images */}
                        {ret.images && ret.images.length > 0 && (
                          <div className="mt-4">
                            <h4 className="font-medium text-gray-700 mb-2">Customer Images</h4>
                            <div className="flex gap-2 overflow-x-auto">
                              {ret.images.map((img, i) => (
                                <img key={i} src={img} alt={`Return ${i+1}`} className="w-20 h-20 object-cover rounded-lg border" />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Rejection Reason */}
                        {ret.status === 'rejected' && ret.rejection_reason && (
                          <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                            <p className="text-sm text-red-700">
                              <span className="font-medium">Rejection Reason:</span> {ret.rejection_reason}
                            </p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="mt-4 flex flex-wrap gap-2">
                          {ret.status === 'pending' && (
                            <>
                              <Button
                                onClick={() => handleApprove(ret.id)}
                                disabled={actionLoading === ret.id}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                {actionLoading === ret.id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Approve Return
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => setShowRejectModal(ret.id)}
                                disabled={actionLoading === ret.id}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                            </>
                          )}

                          {ret.status === 'approved' && (
                            <Button
                              onClick={() => handleComplete(ret.id)}
                              disabled={actionLoading === ret.id}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {actionLoading === ret.id ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <IndianRupee className="w-4 h-4 mr-2" />
                              )}
                              Process Refund
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md bg-white shadow-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Reject Return
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Please provide a reason for rejecting this return request.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  className="w-full p-3 border rounded-lg min-h-[100px] focus:ring-2 focus:ring-red-200 focus:border-red-400"
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRejectModal(null);
                      setRejectReason('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleReject(showRejectModal)}
                    disabled={actionLoading === showRejectModal || !rejectReason.trim()}
                  >
                    {actionLoading === showRejectModal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Confirm Rejection
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </VendorLayout>
  );
}
